import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { Search, ShieldBan, ShieldCheck, AlertTriangle, Clock, Mail, Trash2, Image, Crown } from 'lucide-react';
import { apiClient } from '@/lib/api-client';
import toast from 'react-hot-toast';

type Filter = 'all' | 'banned' | 'suspended' | 'unverified';

function Badge({ label, color }: { label: string; color: string }) {
  return <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${color}`}>{label}</span>;
}

export default function AdminUsersPage() {
  const qc = useQueryClient();
  const [search, setSearch]     = useState('');
  const [filter, setFilter]     = useState<Filter>('all');
  const [page, setPage]         = useState(1);
  const [selected, setSelected] = useState<any>(null);
  const [actionModal, setActionModal] = useState<{ type: string; user: any } | null>(null);
  const [reason, setReason]     = useState('');
  const [days, setDays]         = useState('7');

  const { data, isLoading } = useQuery(
    ['admin-users', search, filter, page],
    () => apiClient.get<any>('/admin/users', { params: { search, filter, page, limit: 25 } }),
    { keepPreviousData: true }
  );

  const { data: userDetail } = useQuery(
    ['admin-user', selected?.id],
    () => apiClient.get<any>(`/admin/users/${selected.id}`),
    { enabled: !!selected }
  );

  const onSuccess = () => {
    qc.invalidateQueries('admin-users');
    if (selected) qc.invalidateQueries(['admin-user', selected.id]);
    toast.success('Done');
    setActionModal(null);
    setReason('');
  };

  const warnMut      = useMutation((v: any) => apiClient.post(`/admin/users/${v.id}/warn`,      { reason: v.reason }),              { onSuccess });
  const suspendMut   = useMutation((v: any) => apiClient.post(`/admin/users/${v.id}/suspend`,   { reason: v.reason, days: v.days }), { onSuccess });
  const unsuspendMut = useMutation((v: any) => apiClient.post(`/admin/users/${v.id}/unsuspend`, {}),                               { onSuccess });
  const banMut       = useMutation((v: any) => apiClient.post(`/admin/users/${v.id}/ban`,       { reason: v.reason }),              { onSuccess });
  const unbanMut     = useMutation((v: any) => apiClient.post(`/admin/users/${v.id}/unban`,     {}),                               { onSuccess });
  const verifyMut    = useMutation((v: any) => apiClient.post(`/admin/users/${v.id}/verify-email`, {}),                            { onSuccess });
  const setAdminMut  = useMutation((v: any) => apiClient.post(`/admin/users/${v.id}/set-admin`,   { is_admin: v.is_admin }),        { onSuccess });
  const rmAvatarMut  = useMutation((v: any) => apiClient.delete(`/admin/users/${v.id}/avatar`),                                    { onSuccess });
  const rmBioMut     = useMutation((v: any) => apiClient.delete(`/admin/users/${v.id}/bio`),                                       { onSuccess });

  const handleAction = () => {
    if (!actionModal) return;
    const uid = actionModal.user.id;
    switch (actionModal.type) {
      case 'warn':      return warnMut.mutate({ id: uid, reason });
      case 'suspend':   return suspendMut.mutate({ id: uid, reason, days: parseInt(days) });
      case 'unsuspend': return unsuspendMut.mutate({ id: uid });
      case 'ban':       return banMut.mutate({ id: uid, reason });
      case 'unban':     return unbanMut.mutate({ id: uid });
      case 'verify':       return verifyMut.mutate({ id: uid });
      case 'grant-admin':  return setAdminMut.mutate({ id: uid, is_admin: true });
      case 'revoke-admin': return setAdminMut.mutate({ id: uid, is_admin: false });
      case 'rm-avatar':    return rmAvatarMut.mutate({ id: uid });
      case 'rm-bio':    return rmBioMut.mutate({ id: uid });
    }
  };

  const needsReason = ['warn','suspend','ban'].includes(actionModal?.type ?? '');
  const users: any[] = data?.users ?? [];
  const total: number = data?.total ?? 0;

  return (
    <div className="p-8">
      <h1 className="text-2xl font-bold mb-1">User Management</h1>
      <p className="text-sm text-gray-500 dark:text-zinc-500 mb-6">{total} users</p>

      <div className="flex flex-wrap gap-3 mb-4">
        <div className="relative flex-1 min-w-48">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
          <input value={search} onChange={e => { setSearch(e.target.value); setPage(1); }}
            placeholder="Search username or email…"
            className="w-full pl-9 pr-3 py-2 text-sm border border-gray-200 dark:border-zinc-700 rounded-lg bg-white dark:bg-zinc-900 focus:outline-none focus:ring-2 focus:ring-brand-400" />
        </div>
        {(['all','banned','suspended','unverified'] as Filter[]).map(f => (
          <button key={f} onClick={() => { setFilter(f); setPage(1); }}
            className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors ${filter === f ? 'bg-brand-600 text-white' : 'bg-white dark:bg-zinc-900 border border-gray-200 dark:border-zinc-700 text-gray-600 dark:text-zinc-400'}`}>
            {f.charAt(0).toUpperCase() + f.slice(1)}
          </button>
        ))}
      </div>

      <div className="flex gap-6">
        <div className="flex-1 bg-white dark:bg-zinc-900 rounded-xl border border-gray-200 dark:border-zinc-800 overflow-hidden">
          {isLoading ? (
            <div className="p-6 space-y-3">{[...Array(8)].map((_,i) => <div key={i} className="h-10 rounded bg-gray-100 dark:bg-zinc-800 animate-pulse" />)}</div>
          ) : (
            <table className="w-full text-sm">
              <thead className="bg-gray-50 dark:bg-zinc-800 text-xs text-gray-500 dark:text-zinc-500 uppercase tracking-wide">
                <tr>
                  <th className="px-4 py-3 text-left">User</th>
                  <th className="px-4 py-3 text-left">Status</th>
                  <th className="px-4 py-3 text-left">Joined</th>
                  <th className="px-4 py-3 text-left">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100 dark:divide-zinc-800">
                {users.map(u => (
                  <tr key={u.id} onClick={() => setSelected(u)}
                    className={`cursor-pointer hover:bg-gray-50 dark:hover:bg-zinc-800 transition-colors ${selected?.id === u.id ? 'bg-brand-50 dark:bg-brand-900/20' : ''}`}>
                    <td className="px-4 py-3">
                      <p className="font-medium">{u.username}</p>
                      <p className="text-xs text-gray-400 dark:text-zinc-600">{u.email}</p>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex flex-wrap gap-1">
                        {u.is_admin     && <Badge label="Admin"      color="bg-brand-100 dark:bg-brand-900/30 text-brand-700 dark:text-brand-300" />}
                        {u.is_banned    && <Badge label="Banned"     color="bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300" />}
                        {u.is_suspended && <Badge label="Suspended"  color="bg-orange-100 dark:bg-orange-900/30 text-orange-700 dark:text-orange-300" />}
                        {!u.email_verified && <Badge label="Unverified" color="bg-gray-100 dark:bg-zinc-800 text-gray-600 dark:text-zinc-400" />}
                        {!u.is_admin && !u.is_banned && !u.is_suspended && u.email_verified && <Badge label="Active" color="bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300" />}
                      </div>
                    </td>
                    <td className="px-4 py-3 text-gray-500 dark:text-zinc-500 text-xs">{new Date(u.created_at).toLocaleDateString()}</td>
                    <td className="px-4 py-3">
                      <div className="flex gap-1" onClick={e => e.stopPropagation()}>
                        <button onClick={() => setActionModal({ type: u.is_admin ? 'revoke-admin' : 'grant-admin', user: u })} title={u.is_admin ? 'Revoke admin' : 'Grant admin'} className={`p-1.5 rounded transition-colors ${u.is_admin ? 'text-brand-600 hover:bg-brand-50 dark:hover:bg-brand-900/20' : 'text-gray-400 hover:bg-gray-100 dark:hover:bg-zinc-800'}`}><Crown className="h-4 w-4" /></button>
                        <button onClick={() => setActionModal({ type: 'warn', user: u })} title="Warn" className="p-1.5 rounded hover:bg-yellow-50 dark:hover:bg-yellow-900/20 text-yellow-600"><AlertTriangle className="h-4 w-4" /></button>
                        {u.is_suspended
                          ? <button onClick={() => setActionModal({ type: 'unsuspend', user: u })} title="Unsuspend" className="p-1.5 rounded hover:bg-green-50 dark:hover:bg-green-900/20 text-green-600"><ShieldCheck className="h-4 w-4" /></button>
                          : <button onClick={() => setActionModal({ type: 'suspend',   user: u })} title="Suspend"   className="p-1.5 rounded hover:bg-orange-50 dark:hover:bg-orange-900/20 text-orange-600"><Clock className="h-4 w-4" /></button>
                        }
                        {u.is_banned
                          ? <button onClick={() => setActionModal({ type: 'unban', user: u })} title="Unban" className="p-1.5 rounded hover:bg-green-50 dark:hover:bg-green-900/20 text-green-600"><ShieldCheck className="h-4 w-4" /></button>
                          : <button onClick={() => setActionModal({ type: 'ban',   user: u })} title="Ban"   className="p-1.5 rounded hover:bg-red-50 dark:hover:bg-red-900/20 text-red-600"><ShieldBan className="h-4 w-4" /></button>
                        }
                        {!u.email_verified && (
                          <button onClick={() => setActionModal({ type: 'verify', user: u })} title="Verify email" className="p-1.5 rounded hover:bg-blue-50 dark:hover:bg-blue-900/20 text-blue-600"><Mail className="h-4 w-4" /></button>
                        )}
                        <button onClick={() => setActionModal({ type: 'rm-avatar', user: u })} title="Remove avatar" className="p-1.5 rounded hover:bg-gray-100 dark:hover:bg-zinc-800 text-gray-500"><Image className="h-4 w-4" /></button>
                        <button onClick={() => setActionModal({ type: 'rm-bio',    user: u })} title="Remove bio"    className="p-1.5 rounded hover:bg-gray-100 dark:hover:bg-zinc-800 text-gray-500"><Trash2 className="h-4 w-4" /></button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
          <div className="flex items-center justify-between px-4 py-3 border-t border-gray-100 dark:border-zinc-800 text-sm text-gray-500 dark:text-zinc-500">
            <span>{total} total</span>
            <div className="flex gap-2">
              <button disabled={page === 1} onClick={() => setPage(p => p - 1)} className="px-3 py-1 rounded border border-gray-200 dark:border-zinc-700 disabled:opacity-40">Prev</button>
              <span className="px-3 py-1">Page {page}</span>
              <button disabled={page * 25 >= total} onClick={() => setPage(p => p + 1)} className="px-3 py-1 rounded border border-gray-200 dark:border-zinc-700 disabled:opacity-40">Next</button>
            </div>
          </div>
        </div>

        {/* Detail panel */}
        {selected && userDetail && (
          <div className="w-72 flex-shrink-0 bg-white dark:bg-zinc-900 rounded-xl border border-gray-200 dark:border-zinc-800 p-5 space-y-4 self-start">
            <h2 className="font-semibold">{selected.username}</h2>
            <div className="text-xs text-gray-500 dark:text-zinc-500 space-y-1">
              <p>{selected.email}</p>
              <p>Sales: {selected.total_sales ?? 0} · Purchases: {selected.total_purchases ?? 0}</p>
              <p>Warnings: {selected.warning_count ?? 0}</p>
              {selected.ban_reason       && <p className="text-red-500">Ban: {selected.ban_reason}</p>}
              {selected.suspension_reason && <p className="text-orange-500">Suspension: {selected.suspension_reason}</p>}
            </div>
            {userDetail.warnings?.length > 0 && (
              <div>
                <p className="text-xs font-semibold text-gray-500 dark:text-zinc-500 uppercase mb-2">Warnings</p>
                <div className="space-y-2">
                  {userDetail.warnings.map((w: any) => (
                    <div key={w.id} className="text-xs bg-yellow-50 dark:bg-yellow-900/20 rounded-lg p-2">
                      <p className="font-medium">{w.reason}</p>
                      <p className="text-gray-400 dark:text-zinc-600">{w.admin_username} · {new Date(w.created_at).toLocaleDateString()}</p>
                    </div>
                  ))}
                </div>
              </div>
            )}
            {userDetail.history?.length > 0 && (
              <div>
                <p className="text-xs font-semibold text-gray-500 dark:text-zinc-500 uppercase mb-2">Admin History</p>
                <div className="space-y-1">
                  {userDetail.history.map((h: any) => (
                    <p key={h.id} className="text-xs text-gray-500 dark:text-zinc-500">{h.action} · {new Date(h.created_at).toLocaleDateString()}</p>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Action modal */}
      {actionModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50" onClick={() => { setActionModal(null); setReason(''); }}>
          <div className="bg-white dark:bg-zinc-900 rounded-2xl p-6 w-96 shadow-2xl" onClick={e => e.stopPropagation()}>
            <h3 className="font-bold text-lg mb-4 capitalize">{actionModal.type.replace('-', ' ')} — {actionModal.user.username}</h3>
            {needsReason && (
              <textarea value={reason} onChange={e => setReason(e.target.value)}
                placeholder="Reason (required)…" rows={3}
                className="w-full border border-gray-200 dark:border-zinc-700 rounded-xl px-3 py-2 text-sm mb-3 resize-none focus:outline-none focus:ring-2 focus:ring-brand-400 bg-white dark:bg-zinc-800" />
            )}
            {actionModal.type === 'suspend' && (
              <div className="flex items-center gap-2 mb-3">
                <label className="text-sm text-gray-600 dark:text-zinc-400">Days:</label>
                <input type="number" value={days} onChange={e => setDays(e.target.value)} min="1" max="365"
                  className="w-20 border border-gray-200 dark:border-zinc-700 rounded-lg px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-brand-400 bg-white dark:bg-zinc-800" />
              </div>
            )}
            <div className="flex gap-3 mt-2">
              <button onClick={() => { setActionModal(null); setReason(''); }} className="flex-1 px-4 py-2 rounded-xl border border-gray-200 dark:border-zinc-700 text-sm font-medium hover:bg-gray-50 dark:hover:bg-zinc-800 transition-colors">Cancel</button>
              <button onClick={handleAction} disabled={needsReason && !reason.trim()} className="flex-1 px-4 py-2 rounded-xl bg-brand-600 text-white text-sm font-medium hover:bg-brand-700 disabled:opacity-50 transition-colors">Confirm</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
